import { useEffect, useState } from 'react';
import { Box, Stack, IconButton, TextField, Typography } from '@mui/material';
import CheckIcon from '@mui/icons-material/Check';
import CloseIcon from '@mui/icons-material/Close';
import EditIcon from '@mui/icons-material/Edit';
import colorFinder from '../../../utils/colorFinder';

const WorkspaceEditableText = ({ value, onSave, sx = {}, multiline = false, showEditIcon = false }) => {
  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [inputValue, setInputValue] = useState(value ?? '');

  useEffect(() => {
    setInputValue(value ?? '');
  }, [value]);

  const handleCancel = () => {
    setInputValue(value ?? '');
    setEditing(false);
  };

  const handleSave = async () => {
    if (!inputValue.trim() || inputValue === value) {
      handleCancel();
      return;
    }

    setSaving(true);

    try {
      await onSave(inputValue);
      setEditing(false);
    } catch (error) {
    } finally {
      setSaving(false);
    }
  };

  const saveIconColor = colorFinder('green');
  const closeIconColor = colorFinder('red');

  return editing ? (
    <Stack direction="row" alignItems="center" spacing={0.5} sx={{ flex: 1, minWidth: 0 }}>
      <TextField
        autoFocus
        fullWidth
        size="small"
        multiline={multiline}
        rows={multiline ? 2 : undefined}
        value={inputValue}
        onChange={(e) => setInputValue(e.target.value)}
        disabled={saving}
      />

      <IconButton
        onClick={handleSave}
        disabled={saving}
        sx={{
          bgcolor: saveIconColor.colorBgCode,
          color: saveIconColor.textColor,
          borderRadius: 1,
          border: '1px solid transparent',
          '&:hover': {
            bgcolor: saveIconColor.colorBgCode,
            borderColor: saveIconColor.textColor,
          },
        }}
      >
        <CheckIcon />
      </IconButton>

      <IconButton
        onClick={handleCancel}
        disabled={saving}
        sx={{
          bgcolor: closeIconColor.colorBgCode,
          color: closeIconColor.textColor,
          borderRadius: 1,
          border: '1px solid transparent',
          '&:hover': {
            bgcolor: closeIconColor.colorBgCode,
            borderColor: closeIconColor.textColor,
          },
        }}
      >
        <CloseIcon />
      </IconButton>
    </Stack>
  ) : (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'flex-start',
        justifyContent: 'space-between',
        gap: 0.5,
        minWidth: 0,
        flex: 1,
      }}
    >
      <Typography
        onClick={() => setEditing(true)}
        sx={{
          cursor: 'pointer',
          minWidth: 0,
          overflowWrap: 'anywhere',
          whiteSpace: multiline ? 'pre-line' : 'normal',
          ...sx,
        }}
      >
        {inputValue}
      </Typography>

      {showEditIcon && (
        <IconButton size="small" sx={{ p: 0.5, flexShrink: 0 }} onClick={() => setEditing(true)}>
          <EditIcon fontSize="small" />
        </IconButton>
      )}
    </Box>
  );
};

export default WorkspaceEditableText;
